import React, { useState, useRef, useEffect, useCallback } from 'react'
import Webcam from 'react-webcam'
import PhotoRealisticAvatar from './PhotoRealisticAvatar'
import Avatar from './Avatar'
import VisitorRegistration from './VisitorRegistration'
import EditableFloorMap from './EditableFloorMap'
import ErrorBoundary from './ErrorBoundary'
import { alsokTheme, componentThemes } from '../styles/alsok-theme'
import { avatarService } from '../services/avatarService'
import { receptionStatusManager } from '../services/receptionStatusManager'
import { chatHistoryManager } from '../services/chatHistoryManager'
import { useReceptionStatus } from '../hooks/useReceptionStatus'
import { usePlanFeatures } from '../hooks/usePlanFeatures'
import pcNotificationService from '../services/pcNotificationService'

const ReceptionLayout = ({ 
  avatarType = 'photo',
  selectedAvatarId = 'sato',
  currentUser = null,
  receptionStatus = { status: 'active' }, // 後方互換性のため残すが、グローバル状態を優先使用
  onNavigateToAdmin = null
}) => {
  const webcamRef = useRef(null)
  const avatarRef = useRef(null)
  const chatHistoryRef = useRef(null)
  
  const [speechText, setSpeechText] = useState('')
  const [faceResult, setFaceResult] = useState('')
  const [isListening, setIsListening] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [error, setError] = useState('')
  const [avatarMessage, setAvatarMessage] = useState('')
  const [avatarStatus, setAvatarStatus] = useState('ready')
  const [faceAuthEnabled, setFaceAuthEnabled] = useState(true)
  const [currentVisitor, setCurrentVisitor] = useState(null)
  const [currentAudio, setCurrentAudio] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  
  // グローバル受付状態の管理
  const receptionStatusHook = useReceptionStatus()
  const currentReceptionStatus = receptionStatusHook.status
  
  // プラン機能の管理
  const { currentPlan, planFeatures, features } = usePlanFeatures()
  
  // PC通知の初期化（梅プランのみ）
  useEffect(() => {
    if (features.emailNotification && currentPlan === 'ume') {
      // 梅プランでPC通知の権限要求
      const initNotifications = async () => {
        try {
          const settings = pcNotificationService.loadSettings()
          if (!settings.enabled && pcNotificationService.isSupported) {
            console.log('🔔 梅プラン: PC通知機能が利用可能です')
          }
        } catch (error) {
          console.warn('PC通知初期化エラー:', error)
        }
      }
      initNotifications()
    }
  }, [features.emailNotification, currentPlan])
  
  const [chatHistory, setChatHistory] = useState(() => {
    // 新しいメッセージを反映するため、履歴をクリアして初期化
    console.log('🔄 ReceptionLayout: 履歴を初期化します')
    chatHistoryManager.clearHistory()
    
    // 既存の履歴は使用せず、常に新しい初期メッセージを設定
    const globalChatHistory = chatHistoryManager.getChatHistory()
    
    // 初回起動時のみ受付状態に応じた初期メッセージを設定
    const globalStatus = receptionStatusManager.getStatus()
    const initialStatusInfo = globalStatus.status === 'active' 
      ? { message: 'ようこそお越しくださいました。\nアルソック株式会社、受付AIでございます。\n本日はご来社いただき、誠にありがとうございます。' }
      : globalStatus.status === 'stopped'
      ? { message: '申し訳ございません。現在受付を停止しております。' }
      : globalStatus.status === 'maintenance'
      ? { message: globalStatus.maintenanceMessage || '現在システムメンテナンス中です。しばらくお待ちください。' }
      : { message: '現在システムメンテナンス中です。しばらくお待ちください。' }
    
    const initialHistory = [
      {
        type: 'ai',
        message: initialStatusInfo.message,
        timestamp: new Date()
      }
    ]
    
    // グローバル履歴に初期化（初回のみ）
    console.log('🆕 ReceptionLayout: 新しい履歴を初期化')
    chatHistoryManager.initializeHistory(initialStatusInfo.message)
    
    return initialHistory
  })
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(false)
  const [registeredVisitors, setRegisteredVisitors] = useState([])
  const [isMapOpen, setIsMapOpen] = useState(false)
  const [aiButtonPosition, setAiButtonPosition] = useState(() => {
    // 初期位置を右下に設定
    const buttonWidth = 160
    const buttonHeight = 50
    return {
      x: Math.max(0, (window.innerWidth || 1200) - buttonWidth - 20),
      y: Math.max(0, (window.innerHeight || 800) - buttonHeight - 20)
    }
  })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  // 受付状態に応じた情報を取得
  const getReceptionStatusInfo = () => {
    switch (currentReceptionStatus.status) {
      case 'active':
        return {
          icon: '✅',
          label: '受付中',
          color: '#22c55e',
          bgColor: '#dcfce7',
          message: 'AI受付システムが稼働しています。お気軽にお声がけください。',
          isEnabled: true
        }
      case 'stopped':
        return {
          icon: '⛔️',
          label: '受付停止中',
          color: '#ef4444',
          bgColor: '#fef2f2',
          message: '申し訳ございません。現在受付を停止しております。',
          isEnabled: false
        }
      case 'maintenance':
        return {
          icon: '🛠️',
          label: 'メンテナンス中',
          color: '#f59e0b',
          bgColor: '#fef3c7',
          message: currentReceptionStatus.maintenanceMessage || '現在システムメンテナンス中です。しばらくお待ちください。',
          isEnabled: false
        }
      default:
        return {
          icon: '❓',
          label: '状態不明',
          color: '#6b7280',
          bgColor: '#f3f4f6',
          message: 'システムの状態を確認中です。',
          isEnabled: false
        }
    }
  }
  const [clickTimeout, setClickTimeout] = useState(null)
  const [hasDragged, setHasDragged] = useState(false)
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 })
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  
  // グローバル状態変更の監視（チャット重複を防ぐため初回ロードのフラグを使用）
  const [isInitialMount, setIsInitialMount] = useState(true)
  
  useEffect(() => {
    console.log('📡 ReceptionLayout: グローバル状態リスナーを登録中...')
    
    const unsubscribe = receptionStatusManager.addListener((event) => {
      console.log('🔄 ReceptionLayout: グローバル状態変更を受信:', event.current)
      
      const previousStatus = currentReceptionStatus.status
      // 状態はグローバルフックから取得されるので、ローカル更新は不要
      // setCurrentReceptionStatus(event.current)
      
      // 初回マウント時または実際に状態が変わった場合のみメッセージを追加
      if (!isInitialMount && previousStatus !== event.current.status) {
        const newStatus = event.current.status
        
        // メンテナンスまたは停止状態に入った場合、会話ログをクリア
        if (newStatus === 'maintenance' || newStatus === 'stopped') {
          console.log('🗑️ ReceptionLayout: メンテナンス/停止状態のため会話ログをクリア')
          chatHistoryManager.clearHistory()
        }
        
        const newMessage = newStatus === 'active' 
          ? 'ようこそお越しくださいました。\nアルソック株式会社、受付AIでございます。\n本日はご来社いただき、誠にありがとうございます。'
          : newStatus === 'stopped'
          ? '申し訳ございません。現在受付を停止しております。'
          : newStatus === 'maintenance'
          ? (event.current.maintenanceMessage || '現在システムメンテナンス中です。しばらくお待ちください。')
          : '現在システムメンテナンス中です。しばらくお待ちください。'
        
        // メンテナンスから通常状態に戻った場合、復旧メッセージを追加
        if (previousStatus === 'maintenance' && newStatus === 'active') {
          console.log('✅ ReceptionLayout: メンテナンスから復旧')
          const recoveryMessage = {
            type: 'ai',
            message: 'メンテナンスが完了しました。通常通りご利用いただけます。',
            timestamp: new Date()
          }
          // 少し遅延して追加（状態変更後に確実に追加するため）
          setTimeout(() => {
            chatHistoryManager.addMessage(recoveryMessage)
          }, 100)
        } else {
          const messages = [
            {
              type: 'system',
              message: `受付状態が変更されました`,
              timestamp: new Date()
            },
            {
              type: 'ai',
              message: newMessage,
              timestamp: new Date()
            }
          ]
          
          addChatMessages(messages)
        }
        
        setAvatarMessage(newMessage)
      }
    })
    
    // 初回マウント完了をマーク
    setIsInitialMount(false)
    
    return () => {
      console.log('🗑️ ReceptionLayout: グローバル状態リスナーを削除')
      unsubscribe()
    }
  }, [])

  // チャット履歴の同期
  useEffect(() => {
    console.log('📡 ReceptionLayout: チャット履歴リスナーを登録中...')
    
    // マウント時に最新の履歴を取得
    const latestHistory = chatHistoryManager.getChatHistory()
    if (latestHistory.length > 0) {
      console.log('🔄 ReceptionLayout: マウント時に最新履歴を取得:', latestHistory.length, '件')
      setChatHistory([...latestHistory])
    }
    
    const unsubscribe = chatHistoryManager.addListener((event) => {
      console.log('🔄 ReceptionLayout: チャット履歴変更を受信:', event.type, '履歴件数:', event.history.length)
      
      // 全てのイベントでローカル状態を更新（確実な同期のため）
      if (event.type === 'messageAdded' || event.type === 'messagesAdded' || event.type === 'historyCleared' || event.type === 'historyInitialized' || event.type === 'historyUpdatedFromStorage') {
        console.log('📝 ReceptionLayout: ローカル状態を更新中...', event.type)
        setChatHistory([...event.history])
      }
    })
    
    return () => {
      console.log('🗑️ ReceptionLayout: チャット履歴リスナーを削除')
      unsubscribe()
    }
  }, [])
  
  // デバッグ用: currentVisitor変更時のログ
  useEffect(() => {
    console.log('Current visitor changed:', currentVisitor)
  }, [currentVisitor])

  // チャット履歴更新のヘルパー関数
  const addChatMessage = (message) => {
    const newMessage = {
      type: message.type || 'ai',
      message: message.message || '',
      timestamp: message.timestamp || new Date()
    }
    
    // グローバル状態を先に更新（リスナーを通じて他のコンポーネントとローカル状態を同期）
    chatHistoryManager.addMessage(newMessage)
  }

  const addChatMessages = (messages) => {
    const newMessages = messages.map(msg => ({
      type: msg.type || 'ai',
      message: msg.message || '',
      timestamp: msg.timestamp || new Date()
    }))
    
    // グローバル状態を先に更新（リスナーを通じて他のコンポーネントとローカル状態を同期）
    chatHistoryManager.addMessages(newMessages)
  }

  // 受付状態変更時のアバターメッセージのみ更新（チャット重複を防ぐため削除）
  useEffect(() => {
    const statusInfo = getReceptionStatusInfo()
    setAvatarMessage(statusInfo.message)
    console.log('Reception status changed:', currentReceptionStatus)
  }, [currentReceptionStatus.status])

  // チャット履歴更新時の自動スクロール
  useEffect(() => {
    if (chatHistoryRef.current) {
      // 少し遅延を入れてスムーズにスクロール
      setTimeout(() => {
        // 再度参照の存在確認
        if (chatHistoryRef.current && chatHistoryRef.current.scrollTo) {
          chatHistoryRef.current.scrollTo({
            top: chatHistoryRef.current.scrollHeight,
            behavior: 'smooth'
          })
        }
      }, 100)
    }
  }, [chatHistory])
  
  // 来客登録処理
  const handleVisitorRegistration = (registrationData) => {
    setRegisteredVisitors(prev => [...prev, registrationData])
    
    // チャット履歴に登録完了を追加
    addChatMessages([
      {
        type: 'system',
        message: `${registrationData.visitorName}様（${registrationData.company}）の来客登録が完了しました。`
      },
      {
        type: 'ai',
        message: `${registrationData.visitorName}様、ご登録ありがとうございます。担当の${registrationData.contactPerson}様にご連絡いたします。しばらくお待ちください。`
      }
    ])
    
    setAvatarMessage(`${registrationData.visitorName}様、来客登録が完了いたしました。担当者にご連絡いたしますので、しばらくお待ちください。`)
    
    // 手動スクロール追加（確実にスクロールするため）
    setTimeout(() => {
      if (chatHistoryRef.current) {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
      }
    }, 200)
  }

  // 内線電話処理
  const handleExtensionCall = () => {
    addChatMessages([
      {
        type: 'user',
        message: '内線電話を使いたいです'
      },
      {
        type: 'ai',
        message: '内線電話をご利用の場合は、受付スタッフまでお声がけください。こちらでおつなぎいたします。'
      }
    ])
    setAvatarMessage('内線電話をご利用の場合は、受付スタッフまでお声がけください。こちらでおつなぎいたします。')
    
    // 手動スクロール追加（確実にスクロールするため）
    setTimeout(() => {
      if (chatHistoryRef.current) {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
      }
    }, 200)
  }

  // 管理者に連絡機能
  const handleContactAdmin = () => {
    const timestamp = new Date()
    
    // チャット履歴に追加
    addChatMessages([
      {
        type: 'user',
        message: '管理者に連絡'
      },
      {
        type: 'ai',
        message: '管理者への緊急連絡を送信いたします。管理者が確認次第、すぐに対応いたします。しばらくお待ちください。'
      }
    ])
    
    setAvatarMessage('管理者への緊急連絡を送信いたします。管理者が確認次第、すぐに対応いたします。')
    
    // 通知データを作成
    const notificationData = {
      id: Date.now(),
      type: 'admin-contact',
      message: 'お客様から管理者への連絡要請があります',
      timestamp: timestamp,
      location: '受付エリア',
      priority: 'high'
    }
    
    // ローカルストレージに通知を保存（管理画面で読み取り可能）
    const existingNotifications = JSON.parse(localStorage.getItem('adminNotifications') || '[]')
    localStorage.setItem('adminNotifications', JSON.stringify([...existingNotifications, notificationData]))
    
    // カスタムイベントを発火して通知
    console.log('Dispatching admin notification:', notificationData) // デバッグログ
    window.dispatchEvent(new CustomEvent('adminNotification', { detail: notificationData }))
    
    // 手動スクロール追加（確実にスクロールするため）
    setTimeout(() => {
      if (chatHistoryRef.current) {
        chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
      }
    }, 200)
  }

  // ヘルプ・操作説明ボタンの処理
  const handleHelpButtonClick = () => {
    if (hasDragged) {
      setHasDragged(false)
      return
    }
    
    setIsHelpModalOpen(true)
    addChatMessage({
      type: 'system',
      message: 'ヘルプ・操作説明を表示しました。'
    })
  }

  // ヘルプボタンのドラッグ処理
  const handleHelpMouseDown = (e) => {
    setIsDragging(true)
    setHasDragged(false)
    setInitialMousePos({ x: e.clientX, y: e.clientY })
    const rect = e.target.getBoundingClientRect()
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    })
    e.preventDefault()
  }

  const handleHelpMouseMove = useCallback((e) => {
    if (!isDragging) return
    
    const deltaX = Math.abs(e.clientX - initialMousePos.x)
    const deltaY = Math.abs(e.clientY - initialMousePos.y)
    
    if (deltaX > 5 || deltaY > 5) {
      setHasDragged(true)
    }
    
    const buttonWidth = 120
    const buttonHeight = 50
    
    setAiButtonPosition({
      x: Math.max(0, Math.min(window.innerWidth - buttonWidth, e.clientX - dragOffset.x)),
      y: Math.max(0, Math.min(window.innerHeight - buttonHeight, e.clientY - dragOffset.y))
    })
  }, [isDragging, dragOffset, initialMousePos])

  const handleHelpMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleHelpMouseMove)
      document.addEventListener('mouseup', handleHelpMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleHelpMouseMove)
        document.removeEventListener('mouseup', handleHelpMouseUp)
      }
    }
  }, [isDragging, handleHelpMouseMove, handleHelpMouseUp])



  // 時刻更新
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  // 初期化メッセージ
  useEffect(() => {
    const statusInfo = getReceptionStatusInfo()
    if (avatarType === 'photo' && avatarService.isLoaded) {
      setAvatarMessage(statusInfo.message)
    } else {
      setAvatarMessage(statusInfo.message)
    }
  }, [avatarType, selectedAvatarId, currentReceptionStatus.status])

  // Webカメラからの画像キャプチャ
  const captureImage = useCallback(() => {
    if (!webcamRef.current) {
      console.error('Webcam ref is not available')
      return null
    }
    
    try {
      const imageSrc = webcamRef.current.getScreenshot()
      return imageSrc
    } catch (error) {
      console.error('Failed to capture image:', error)
      return null
    }
  }, [webcamRef])

  // 音声認識開始
  const startSpeechRecognition = async () => {
    // プラン制御: 音声受付機能チェック
    if (!features.voiceReception) {
      const errorMsg = `${planFeatures.name}では音声受付機能は利用できません。`
      setError(errorMsg)
      setAvatarMessage(errorMsg)
      return
    }
    
    // 受付状態が無効の場合は音声認識を開始しない
    if (!getReceptionStatusInfo().isEnabled) {
      setError('現在受付が停止しているため、音声認識を利用できません。')
      setAvatarMessage(getReceptionStatusInfo().message)
      return
    }
    
    try {
      console.log('🎤 音声認識を開始します...')
      setIsListening(true)
      setAvatarStatus('listening')
      setAvatarMessage('お話しください...')
      setError('')
      
      // ブラウザサポートチェック
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        throw new Error('このブラウザは音声認識をサポートしていません。Chrome、Edge、Safariをご利用ください。')
      }
      
      // HTTPS必須チェック
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        throw new Error('音声認識はHTTPS接続またはlocalhostでのみ利用可能です。')
      }
      
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
      const recognition = new SpeechRecognition()
      
      console.log('🎤 音声認識オブジェクトを作成しました')
      
      recognition.lang = 'ja-JP'
      recognition.continuous = false
      recognition.interimResults = false
      recognition.maxAlternatives = 1
      
      // マイクの権限チェック
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(track => track.stop())
        console.log('🎤 マイクの権限を確認しました')
      } catch (permissionError) {
        console.error('🎤 マイクの権限エラー:', permissionError)
        throw new Error('マイクへのアクセス権限が必要です。ブラウザの設定でマイクを許可してください。')
      }
      
      recognition.onresult = async (event) => {
        const speechResult = event.results[0][0].transcript
        const confidence = event.results[0][0].confidence
        
        console.log('🎤 音声認識結果:', speechResult, '信頼度:', confidence)
        setSpeechText(`認識結果: ${speechResult}`)
        
        // ユーザーの発言をチャット履歴に追加
        addChatMessage({
          type: 'user',
          message: speechResult
        })
        
        // スクロール処理追加
        setTimeout(() => {
          if (chatHistoryRef.current) {
            chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
          }
        }, 100)
        
        setAvatarMessage(`「${speechResult}」と伺いました。`)
        
        // PC通知（梅プランのみ）
        if (features.emailNotification && currentPlan === 'ume') {
          pcNotificationService.notifyVoiceRecognition({
            transcript: speechResult,
            confidence: confidence
          })
        }
        
        // 応答生成とTTS
        try {
          setIsSpeaking(true)
          setAvatarStatus('speaking')
          
          console.log('🤖 generateResponse を呼び出します。入力:', speechResult)
          const response = generateResponse(speechResult)
          console.log('🤖 generateResponse の結果:', response)
          setAvatarMessage(response)
          
          // AIの応答をチャット履歴に追加
          setTimeout(() => {
            addChatMessage({
              type: 'ai',
              message: response
            })
            
            // スクロール処理追加
            setTimeout(() => {
              if (chatHistoryRef.current) {
                chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
              }
            }, 100)
            
            setIsSpeaking(false)
            setAvatarStatus('ready')
            setAvatarMessage('他にご用件はございますか？')
          }, 2000)
          
        } catch (ttsError) {
          console.error('TTS Error:', ttsError)
          setIsSpeaking(false)
          setAvatarStatus('ready')
        }
      }
      
      recognition.onerror = (event) => {
        console.error('🎤 音声認識エラー:', event.error)
        let errorMessage = '音声認識でエラーが発生しました。'
        
        switch(event.error) {
          case 'network':
            errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください。'
            break
          case 'not-allowed':
            errorMessage = 'マイクへのアクセス権限が拒否されました。ブラウザの設定でマイクを許可してください。'
            break
          case 'no-speech':
            errorMessage = '音声が検出されませんでした。もう一度お話しください。'
            break
          case 'audio-capture':
            errorMessage = 'マイクにアクセスできません。他のアプリケーションがマイクを使用していないか確認してください。'
            break
          case 'service-not-allowed':
            errorMessage = '音声認識サービスが利用できません。HTTPS接続を確認してください。'
            break
          default:
            errorMessage = `音声認識エラー: ${event.error}`
        }
        
        setError(errorMessage)
        setIsListening(false)
        setAvatarStatus('error')
        setAvatarMessage(errorMessage)
      }
      
      recognition.onstart = () => {
        console.log('🎤 音声認識が開始されました')
        setAvatarMessage('お話しください...')
      }
      
      recognition.onend = () => {
        console.log('🎤 音声認識が終了しました')
        setIsListening(false)
        if (avatarStatus === 'listening') {
          setAvatarStatus('ready')
        }
      }
      
      console.log('🎤 音声認識を開始しています...')
      recognition.start()
      
    } catch (err) {
      console.error('Speech recognition start error:', err)
      setError(err.message)
      setIsListening(false)
      setAvatarStatus('error')
      setAvatarMessage('音声認識を開始できませんでした。')
    }
  }

  // 応答生成（より自然で詳細な応答）
  const generateResponse = (userInput) => {
    const input = userInput.toLowerCase()
    console.log('🤖 入力された音声:', userInput)
    
    // 音声認識の応答を改善するため、一時的にavatarServiceを無効化
    if (false && avatarType === 'photo' && avatarService.isLoaded) {
      console.log('🤖 avatarService.generateContextualResponse を使用します')
      let context = 'general_inquiry'
      
      if (input.includes('会議室') || input.includes('予約')) {
        context = 'meeting_room'
      } else if (input.includes('来訪') || input.includes('面会') || input.includes('約束')) {
        context = 'visitor_registration'
      }
      
      console.log('🤖 avatarService コンテキスト:', context)
      const avatarResponse = avatarService.generateContextualResponse(selectedAvatarId, context, userInput)
      console.log('🤖 avatarService 応答:', avatarResponse)
      return avatarResponse
    }
    
    // 具体的な人名・会社名の検出
    if (input.includes('田中') || input.includes('a社') || input.includes('えー社')) {
      return 'A社の田中様でいらっしゃいますね。本日はお忙しい中お越しいただきありがとうございます。担当の鈴木へすぐにご連絡いたします。会議室Aでお待ちいただけますでしょうか。'
    }
    
    if (input.includes('佐藤') || input.includes('b社') || input.includes('びー社')) {
      return 'B社の佐藤様でいらっしゃいますね。担当の高橋へご連絡いたします。会議室Bへご案内いたします。'
    }
    
    if (input.includes('鈴木') || input.includes('すずき')) {
      return '鈴木様への面会でいらっしゃいますね。鈴木様へお客様がお見えになった旨をお伝えいたします。少々お待ちください。'
    }
    
    if (input.includes('高橋') || input.includes('たかはし')) {
      return '高橋様への面会でいらっしゃいますね。高橋様へお客様がお見えになった旨をお伝えいたします。少々お待ちください。'
    }
    
    // あいさつ・初回対応
    if (input.includes('こんにちは') || input.includes('こんばんは') || input.includes('おはよう')) {
      const hour = new Date().getHours()
      let greeting = 'こんにちは'
      if (hour < 12) greeting = 'おはようございます'
      else if (hour >= 18) greeting = 'こんばんは'
      
      return `${greeting}！本日はお越しいただきありがとうございます。お約束がございましたら、お相手の方のお名前をお聞かせください。`
    }
    
    // 一般的な問い合わせ
    if (input.includes('約束') || input.includes('アポ') || input.includes('会議') || input.includes('打ち合わせ')) {
      return 'お約束の件でお越しいただいたのですね。担当者のお名前か部署名をお教えいただけますでしょうか。すぐにご連絡いたします。'
    }
    
    if (input.includes('面会') || input.includes('面談') || input.includes('訪問')) {
      return '面会のご予定でいらっしゃいますね。どちらの方にお会いになりますでしょうか。お名前をお教えください。'
    }
    
    // 施設案内
    if (input.includes('トイレ') || input.includes('お手洗い') || input.includes('化粧室')) {
      return 'お手洗いは、こちらの廊下を真っ直ぐ進んで左手にございます。男性用、女性用、多目的トイレがございます。'
    }
    
    if (input.includes('駐車場') || input.includes('駐車')) {
      return '駐車場は建物の裏手にございます。無料でご利用いただけます。お帰りの際に駐車券をお持ちください。'
    }
    
    if (input.includes('会議室') || input.includes('部屋') || input.includes('予約')) {
      return '会議室のご利用でしたら、画面の「会議室確認」ボタンから空室状況をご確認いただけます。お手伝いいたしましょうか。'
    }
    
    if (input.includes('エレベーター') || input.includes('エレベータ')) {
      return 'エレベーターは、この受付の奥、突き当りの左手にございます。各階のフロアマップもエレベーター前に掲示しております。'
    }
    
    if (input.includes('階段')) {
      return '階段は、エレベーターの右手にございます。非常階段もございますので、お気をつけてご利用ください。'
    }
    
    // 時間・営業時間関連
    if (input.includes('時間') || input.includes('営業') || input.includes('開いて')) {
      return '当社の営業時間は平日9時から18時までとなっております。土日祝日は休業させていただいております。'
    }
    
    // 道案内・場所
    if (input.includes('道') || input.includes('場所') || input.includes('どこ')) {
      return '何かお探しでしょうか。施設内の案内でしたら、お手伝いいたします。お探しの場所をお教えください。'
    }
    
    // 緊急・困った時
    if (input.includes('急ぎ') || input.includes('緊急') || input.includes('至急')) {
      return '承知いたしました。緊急の件でしたら、すぐに担当者にご連絡いたします。どちらの部署の方でしょうか。'
    }
    
    // 質問・問い合わせ
    if (input.includes('質問') || input.includes('聞きたい') || input.includes('教えて')) {
      return 'ご質問がございましたら、お気軽にお聞かせください。お答えできる範囲でお答えいたします。'
    }
    
    // 待つ・待機
    if (input.includes('待つ') || input.includes('待機') || input.includes('待って')) {
      return '承知いたしました。こちらでお待ちいただけますでしょうか。担当者へご連絡いたします。'
    }
    
    // お礼・感謝
    if (input.includes('ありがとう') || input.includes('感謝') || input.includes('すみません')) {
      return 'こちらこそ、ありがとうございます。他にご不明な点がございましたら、お気軽にお声かけください。'
    }
    
    // 名前を聞かれた場合
    if (input.includes('名前') || input.includes('誰') || input.includes('だれ')) {
      return 'アルソック株式会社の受付AIでございます。ご来社いただき、誠にありがとうございます。'
    }
    
    // 会社・組織について
    if (input.includes('会社') || input.includes('alsok') || input.includes('アルソック')) {
      return 'ALSOKは総合セキュリティサービスを提供する会社です。警備、防犯、安全に関するサービスを幅広く展開しております。'
    }
    
    // 不明な場合でも親切に対応
    if (input.includes('わからない') || input.includes('分からない')) {
      return 'ご不明な点がございましたら、詳しくお聞かせください。お手伝いできることがあれば、喜んでサポートいたします。'
    }
    
    // 否定的な応答への対応
    if (input.includes('いいえ') || input.includes('違う') || input.includes('ちがう')) {
      return '申し訳ございません。改めて、どのようなご用件でしょうか。お聞かせください。'
    }
    
    // 肯定的な応答への対応
    if (input.includes('はい') || input.includes('そう') || input.includes('その通り')) {
      return '承知いたしました。それでは、具体的な内容をお聞かせください。'
    }
    
    // デフォルト応答（より親切で具体的に）
    console.log('🤖 デフォルト応答を返します')
    return `「${userInput}」ですね。承知いたしました。担当者のお名前か部署名をお教えいただけますでしょうか。すぐにお繋ぎいたします。`
  }

  // 顔認証開始（顔パス認証）
  const startFaceAuth = async () => {
    // 受付状態が無効の場合は顔認証を開始しない
    if (!getReceptionStatusInfo().isEnabled) {
      setError('現在受付が停止しているため、顔認証を利用できません。')
      setAvatarMessage(getReceptionStatusInfo().message)
      return
    }
    
    try {
      setIsProcessing(true)
      setAvatarStatus('processing')
      setAvatarMessage('顔認証中です。しばらくお待ちください...')
      setError('')
      
      // Webカメラの準備確認
      if (!webcamRef.current) {
        throw new Error('カメラが準備できていません。しばらくお待ちいただいてから再度お試しください。')
      }
      
      const imageSrc = captureImage()
      if (!imageSrc) {
        throw new Error('画像のキャプチャに失敗しました。カメラが正常に動作しているか確認してください。')
      }
      
      // モック: 顔パス認証シミュレーション
      setTimeout(() => {
        // 今日の予約情報をシミュレート
        const mockVisitors = [
          {
            name: '田中太郎',
            company: 'A社',
            meetingTitle: '企画開発部定例会議',
            organizer: '鈴木次郎',
            room: '会議室A',
            time: '13:00-14:00'
          },
          {
            name: '佐藤花子',
            company: 'B社',  
            meetingTitle: '営業部打ち合わせ',
            organizer: '高橋三郎',
            room: '会議室B',
            time: '15:00-16:00'
          },
          {
            name: '山田一郎',
            company: 'C社',
            meetingTitle: '技術開発プロジェクト会議',
            organizer: '木村部長',
            room: '会議室C',
            time: '14:00-15:30'
          },
          {
            name: '鈴木恵美',
            company: 'D社',
            meetingTitle: '新商品企画会議',
            organizer: '加藤課長',
            room: '大会議室',
            time: '10:00-12:00'
          },
          {
            name: '松本和夫',
            company: 'E社',
            meetingTitle: '重要顧客向け提案',
            organizer: '伊藤社長',
            room: '応接室',
            time: '16:00-17:00'
          }
        ]
        
        // ランダムに訪問者を選択（モック）
        const visitor = mockVisitors[Math.floor(Math.random() * mockVisitors.length)]
        
        const authResult = `顔パス認証成功: ${visitor.name}様（${visitor.company}）`
        const aiResponse = `いらっしゃいませ、${visitor.name}様！${visitor.time}からの「${visitor.meetingTitle}」でお越しいただきありがとうございます。担当の${visitor.organizer}を${visitor.room}へお呼びいたします。`
        
        setFaceResult(authResult)
        setAvatarMessage(aiResponse)
        setAvatarStatus('ready')
        setIsProcessing(false)
        
        // 訪問者情報を安全に設定
        try {
          if (visitor && typeof visitor === 'object') {
            setCurrentVisitor(visitor)
            console.log('Current visitor set:', visitor)
          } else {
            console.error('Invalid visitor data:', visitor)
            setCurrentVisitor(null)
          }
        } catch (error) {
          console.error('Error setting current visitor:', error)
          setCurrentVisitor(null)
        }
        
        // 顔認証結果をチャット履歴に追加
        addChatMessages([
          {
            type: 'system',
            message: authResult
          },
          {
            type: 'ai',
            message: aiResponse
          },
          {
            type: 'system',
            message: `担当者（${visitor.organizer}）にTeamsで通知を送信しました。`
          }
        ])
        
        // モック: 担当者への通知と会議室案内
        setTimeout(() => {
          addChatMessage({
            type: 'ai',
            message: `${visitor.organizer}様より「すぐに参ります」とのお返事をいただきました。下の「🗺️ 会議室への道順」ボタンから案内マップをご確認ください。`
          })
          
          // 自動マップ表示を一時的に無効化（デバッグ用）
          // setTimeout(() => {
          //   setIsMapOpen(true)
          // }, 1500)
        }, 3000)
        
      }, 2500)
      
    } catch (err) {
      console.error('Face auth error:', err)
      
      // 安全な状態復旧
      try {
        setError(err.message || '顔認証でエラーが発生しました')
        setAvatarMessage('顔認証でエラーが発生しました。カメラが正常に動作しているか確認して、もう一度お試しください。')
        setAvatarStatus('error')
        setIsProcessing(false)
      } catch (stateError) {
        console.error('Error during state recovery:', stateError)
        // 最低限の復旧処理
        setIsProcessing(false)
      }
    }
  }

  return (
    <ErrorBoundary>
      <div className="reception-container">
      {/* お客様モード表示バー */}
      <div style={{
        background: getReceptionStatusInfo().isEnabled
          ? `linear-gradient(90deg, ${alsokTheme.accent.success} 0%, #2ECC71 100%)`
          : `linear-gradient(90deg, ${getReceptionStatusInfo().color} 0%, ${getReceptionStatusInfo().color}AA 100%)`,
        color: 'white',
        padding: '8px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: alsokTheme.typography.fontSize.sm,
        fontWeight: alsokTheme.typography.fontWeight.bold,
        boxShadow: alsokTheme.shadows.sm
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <span>{getReceptionStatusInfo().icon} お客様受付モード</span>
          <span style={{ 
            background: getReceptionStatusInfo().isEnabled ? '#2E7D32' : getReceptionStatusInfo().color,
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            {getReceptionStatusInfo().label}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            ALSOK受付
          </span>
        </div>
      </div>

      {/* ステータスバー */}
      <div className="status-bar">
        <div className="status-left">
          <div className="logo">🛡️ ALSOK</div>
          <div className="reception-title" data-text="ALSOK AI受付">ALSOK AI受付</div>
        </div>
        <div className="status-right">
          {/* 管理画面遷移ボタン（管理者のみ表示） */}
          {currentUser && currentUser.role !== 'reception-only' && onNavigateToAdmin && (
            <button 
              onClick={onNavigateToAdmin}
              className="admin-access-btn"
              title="管理画面へ移動"
            >
              <span className="admin-icon">⚙️</span>
              <span className="admin-text">管理画面</span>
            </button>
          )}
          
          <div className="current-time">
            {currentTime.toLocaleString('ja-JP', {
              year: 'numeric',
              month: '2-digit', 
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div className="reception-content">
        {/* アバター全画面表示エリア */}
        <div className="avatar-main-area">
          {avatarType === 'photo' ? (
            <PhotoRealisticAvatar 
              ref={avatarRef}
              status={avatarStatus}
              message={avatarMessage}
              isListening={isListening}
              isSpeaking={isSpeaking}
              audioElement={currentAudio}
              avatarId={selectedAvatarId}
              size="fullscreen"
            />
          ) : (
            <Avatar 
              ref={avatarRef}
              status={avatarStatus}
              message={avatarMessage}
              isListening={isListening}
              isSpeaking={isSpeaking}
              audioElement={currentAudio}
              size="fullscreen"
            />
          )}

        </div>

        {/* サイドパネル（カメラとステータス） */}
        <div className="side-panel">
          <div className="camera-section">
            <div className="camera-container">
              <Webcam
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="webcam-feed"
                videoConstraints={{
                  facingMode: 'user'
                }}
                onUserMediaError={(error) => {
                  console.error('Webcam error:', error)
                  setError('カメラへのアクセスができません。ブラウザの設定を確認してください。')
                }}
                onLoadedData={() => {
                  console.log('Webcam loaded successfully')
                }}
              />
            </div>
            <div className="camera-label">顔認証カメラ</div>
          </div>

          {/* 操作ボタン（カメラ直下に配置） */}
          <div className="camera-controls">
            {/* 音声ボタン - プラン制御 */}
            {features.voiceReception && (
              <button 
                onClick={startSpeechRecognition}
                disabled={isListening || isProcessing || !getReceptionStatusInfo().isEnabled}
                className={`camera-control-btn voice-btn ${isListening ? 'active' : ''} ${!getReceptionStatusInfo().isEnabled ? 'disabled' : ''}`}
              >
              <div className="btn-icon">
                {isListening ? '🎙️' : '🎤'}
              </div>
              <div className="btn-text">
                {isListening ? '聞き取り中...' : '音声で話しかける'}
              </div>
              {isListening && <div className="pulse-indicator"></div>}
              </button>
            )}
            
            {faceAuthEnabled && (
              <button 
                onClick={startFaceAuth}
                disabled={isProcessing || !webcamRef.current || !getReceptionStatusInfo().isEnabled}
                className={`camera-control-btn face-btn ${isProcessing ? 'active' : ''} ${!getReceptionStatusInfo().isEnabled ? 'disabled' : ''}`}
              >
                <div className="btn-icon">
                  {isProcessing ? '🔍' : '📸'}
                </div>
                <div className="btn-text">
                  {isProcessing ? '認証中...' : '顔パス認証'}
                </div>
                {isProcessing && <div className="pulse-indicator"></div>}
              </button>
            )}
            
            {/* PC通知設定ボタン（梅プランのみ） */}
            {features.emailNotification && currentPlan === 'ume' && (
              <button 
                onClick={async () => {
                  try {
                    await pcNotificationService.requestPermission()
                    pcNotificationService.notifySystemAlert({
                      message: 'PC通知が有効になりました',
                      severity: 'success'
                    })
                  } catch (error) {
                    pcNotificationService.notifySystemAlert({
                      message: error.message,
                      severity: 'warning'
                    })
                  }
                }}
                className="camera-control-btn notification-btn"
                title="PC通知を有効にする（梅プラン専用）"
              >
                <div className="btn-icon">
                  {pcNotificationService.isEnabled ? '🔔' : '🔕'}
                </div>
                <div className="btn-text">
                  PC通知設定
                </div>
              </button>
            )}
            
            <button 
              onClick={() => window.location.href = '/?mode=room-booking'}
              disabled={isProcessing || (currentUser?.role === 'reception-only' && receptionStatus.status === 'maintenance')}
              className={`camera-control-btn reservation-btn ${
                currentUser?.role === 'reception-only' && receptionStatus.status === 'maintenance' 
                  ? 'disabled-maintenance' 
                  : ''
              }`}
              title={
                currentUser?.role === 'reception-only' && receptionStatus.status === 'maintenance'
                  ? 'メンテナンス中のため利用できません'
                  : '会議室確認画面に移動'
              }
            >
              <div className="btn-icon">📅</div>
              <div className="btn-text">会議室確認</div>
            </button>
          </div>

          {/* 対話履歴表示 */}
          <div className="chat-section">
            <div className="chat-history" ref={chatHistoryRef}>
              {chatHistory.map((entry, index) => (
                <div key={index} className={`chat-message ${entry.type}`}>
                  <div className="message-content">
                    <span className="message-sender">
                      {entry.type === 'user' ? '👤' : 
                       entry.type === 'ai' ? '🤖' : 
                       '🔧'} 
                    </span>
                    {entry.message}
                    <span className="message-time">
                      {entry.timestamp.toLocaleTimeString('ja-JP', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            {error && (
              <div className="error-display">
                <div className="error-icon">⚠️</div>
                <div className="error-text">{error}</div>
              </div>
            )}
          </div>

          {/* 訪問者情報表示（顔パス成功時） */}
          {currentVisitor && (() => {
            try {
              return (
                <div className="visitor-info">
                  <h3>👤 ご来訪情報</h3>
                  <div className="visitor-details">
                    <div className="info-row">
                      <span className="label">お名前:</span>
                      <span className="value">{currentVisitor?.name || 'ゲスト'}様</span>
                    </div>
                    <div className="info-row">
                      <span className="label">会社:</span>
                      <span className="value">{currentVisitor?.company || '—'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">会議:</span>
                      <span className="value">{currentVisitor?.meetingTitle || '—'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">担当者:</span>
                      <span className="value">{currentVisitor?.organizer || '—'}様</span>
                    </div>
                    <div className="info-row">
                      <span className="label">会議室:</span>
                      <span className="value">{currentVisitor?.room || '—'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">時間:</span>
                      <span className="value">{currentVisitor?.time || '—'}</span>
                    </div>
                  </div>
                  <div className="visitor-actions">
                    <button 
                      className="map-btn"
                      onClick={() => {
                        try {
                          console.log('Opening map with visitor:', currentVisitor)
                          setIsMapOpen(true)
                        } catch (error) {
                          console.error('Error opening map:', error)
                          setError('マップの表示でエラーが発生しました')
                        }
                      }}
                    >
                      🗺️ 会議室への道順
                    </button>
                    <button 
                      className="clear-visitor-btn"
                      onClick={() => setCurrentVisitor(null)}
                    >
                      🔄 受付をリセット
                    </button>
                  </div>
                </div>
              )
            } catch (error) {
              console.error('Error rendering visitor info:', error)
              return (
                <div className="visitor-info">
                  <h3>👤 ご来訪情報</h3>
                  <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
                    <p>来訪者情報の表示でエラーが発生しました。</p>
                    <button 
                      onClick={() => setCurrentVisitor(null)}
                      style={{
                        padding: '8px 16px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 受付をリセット
                    </button>
                  </div>
                </div>
              )
            }
          })()}

          {/* クイックアクション */}
          <div className="quick-actions">
            <button className="quick-btn" onClick={handleExtensionCall}>📞 内線電話</button>
            <button className="quick-btn" onClick={() => setIsRegistrationOpen(true)}>📋 来客登録</button>
            <button className="quick-btn admin-contact" onClick={handleContactAdmin}>🚨 管理者に連絡</button>
          </div>
        </div>
      </div>

      {/* 来客登録モーダル */}
      <VisitorRegistration
        isOpen={isRegistrationOpen}
        onClose={() => setIsRegistrationOpen(false)}
        onSubmit={handleVisitorRegistration}
      />

      {/* 会議室案内マップ */}
      <EditableFloorMap
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        targetRoom={currentVisitor?.room || '会議室A'}
        visitorInfo={currentVisitor || { name: 'ゲスト' }}
        onSaveMap={(newMapData) => {
          console.log('地図データが保存されました:', newMapData)
          // 必要に応じてサーバーに送信
        }}
      />

      <style jsx="true">{`
        /* 全体コンテナ - ALSOKスタイル */
        .reception-container {
          min-height: 100vh;
          background: linear-gradient(135deg, 
            #f8fafc 0%, 
            #e2e8f0 25%, 
            #cbd5e1 50%, 
            #94a3b8 75%, 
            #64748b 100%);
          background-size: 400% 400%;
          animation: alsokGradientShift 20s ease infinite;
          display: flex;
          flex-direction: column;
          font-family: 'Hiragino Kaku Gothic ProN', 'Hiragino Sans', 'Yu Gothic', 'Meiryo', sans-serif;
          position: relative;
          overflow-x: hidden;
          overflow-y: auto;
        }

        .reception-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 80%, rgba(30, 64, 175, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(147, 197, 253, 0.04) 0%, transparent 50%);
          pointer-events: none;
        }

        @keyframes alsokGradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        /* ヘッダーデザイン - ALSOKスタイル */
        .reception-header {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
          backdrop-filter: blur(20px);
          border-bottom: 2px solid rgba(30, 64, 175, 0.1);
          padding: 24px 40px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
          z-index: 10;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .header-icon {
          font-size: 42px;
          color: #1e40af;
          filter: drop-shadow(0 2px 4px rgba(30, 64, 175, 0.2));
          animation: alsokPulse 4s ease-in-out infinite;
        }

        @keyframes alsokPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.02); opacity: 0.9; }
        }

        .header-content h1 {
          margin: 0;
          font-size: 32px;
          font-weight: 700;
          color: #1e40af;
          letter-spacing: 0.5px;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }

        .header-subtitle {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 6px;
          font-size: 16px;
          color: #475569;
          font-weight: 500;
        }

        .mode-indicator {
          font-size: 18px;
          color: #3b82f6;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .status-badge.active {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
        }

        .status-badge.stopped {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
        }

        .status-badge.maintenance {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          color: white;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .welcome-section {
          text-align: right;
        }

        .welcome-text {
          font-size: 20px;
          font-weight: 600;
          color: #1e40af;
          margin-bottom: 4px;
        }

        .current-time {
          font-size: 16px;
          color: #64748b;
          font-weight: 500;
          font-family: 'SF Mono', 'Monaco', 'Consolas', 'Courier New', monospace;
        }

        .admin-access-btn {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .admin-access-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(30, 64, 175, 0.4);
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
        }

        .admin-access-btn .btn-icon {
          font-size: 16px;
        }

        /* 来訪者情報カード - ALSOKスタイル */
        .visitor-info-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
          backdrop-filter: blur(20px);
          margin: 0 40px 24px 40px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(30, 64, 175, 0.1);
          overflow: hidden;
          position: relative;
        }

        .visitor-info-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #1e40af 0%, #3b82f6 100%);
        }

        .visitor-card-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 24px 28px 20px 28px;
          border-bottom: 1px solid rgba(30, 64, 175, 0.08);
        }

        .visitor-icon {
          font-size: 28px;
          padding: 16px;
          background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
          border-radius: 12px;
          color: white;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.3);
        }

        .visitor-card-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #1e40af;
          flex: 1;
        }

        .status-indicator {
          padding: 8px 16px;
          border-radius: 16px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-indicator.active {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #3b82f6;
        }

        .visitor-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          padding: 24px 28px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 16px;
          background: rgba(248, 250, 252, 0.6);
          border-radius: 8px;
          border: 1px solid rgba(30, 64, 175, 0.05);
        }

        .detail-label {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.8px;
        }

        .detail-value {
          font-size: 16px;
          font-weight: 600;
          color: #1e40af;
        }

        .reception-layout {
          min-height: 100vh;
          background: ${componentThemes.reception.background};
          font-family: ${alsokTheme.typography.fontFamily.primary};
          display: flex;
          flex-direction: column;
        }

        .status-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 30px;
          background: ${alsokTheme.primary.main};
          backdrop-filter: blur(10px);
          border-bottom: 3px solid ${alsokTheme.primary.light};
          box-shadow: ${alsokTheme.shadows.lg};
        }

        .status-left {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .logo {
          font-size: 24px;
          font-weight: bold;
          color: white;
        }

        .reception-title {
          font-size: 18px;
          color: #ffffff;
          font-weight: bold;
          text-shadow: 
            0 1px 2px rgba(0, 0, 0, 0.8),
            0 2px 4px rgba(0, 0, 0, 0.6),
            0 0 10px rgba(255, 255, 255, 0.3);
          letter-spacing: 0.5px;
          position: relative;
        }

        .reception-title::before {
          content: attr(data-text);
          position: absolute;
          left: 0;
          top: 0;
          z-index: -1;
          color: #FFD700;
          text-shadow: none;
          filter: blur(1px);
        }

        .status-right {
          display: flex;
          align-items: center;
          gap: 25px;
          color: white;
        }

        .admin-access-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, #FF6B35 0%, #E55722 100%);
          color: white;
          border: 2px solid #FF6B35;
          border-radius: 12px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);
        }

        .admin-access-btn:hover {
          background: linear-gradient(135deg, #E55722 0%, #CC4A1A 100%);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
        }

        .admin-icon {
          font-size: 16px;
          animation: adminPulse 2s ease-in-out infinite;
        }

        .admin-text {
          font-weight: bold;
        }

        @keyframes adminPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .current-time {
          font-size: 16px;
          font-weight: bold;
          font-family: 'Courier New', monospace;
        }

        .system-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .status-indicator {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .status-indicator.active {
          background: ${alsokTheme.accent.green};
        }

        .status-text {
          font-size: 14px;
          font-weight: 600;
          color: white;
        }

        /* 受付ステータス - 左側専用 */
        .reception-status {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 16px;
          background: rgba(255, 255, 255, 0.15);
          padding: 8px 16px;
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .reception-status-indicator {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          animation: pulse 2s infinite;
        }

        .reception-status-indicator.active {
          background: #4CAF50;
          box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
        }

        .reception-status-text {
          font-size: 16px;
          font-weight: 700;
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
        }

        .reception-content {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 24px;
          flex: 1;
          min-height: calc(100vh - 140px);
          padding: 0 32px 32px 32px;
          position: relative;
          z-index: 5;
        }

        .avatar-main-area {
          position: relative;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, rgba(30, 64, 175, 0.95) 0%, rgba(59, 130, 246, 0.9) 100%);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(30, 64, 175, 0.15);
          border: 1px solid rgba(255, 255, 255, 0.2);
          overflow: hidden;
        }

        .avatar-main-area::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 60%),
            radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.06) 0%, transparent 60%),
            url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23ffffff" stroke-width="0.3" opacity="0.08"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)" /></svg>');
          pointer-events: none;
        }

        .camera-controls {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 4px;
        }

        .camera-control-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          border: none;
          border-radius: ${alsokTheme.borderRadius.lg};
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: ${alsokTheme.shadows.md};
          width: 100%;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(10px);
          font-size: 13px;
          min-height: 44px;
        }

        /* 音声ボタン - ALSOK情報ブルー */
        .voice-btn {
          background: ${componentThemes.reception.controls.voice.background} !important;
          color: white !important;
          border: 2px solid ${alsokTheme.accent.info} !important;
          box-shadow: ${componentThemes.reception.controls.voice.shadow} !important;
          min-height: ${componentThemes.reception.controls.voice.minHeight} !important;
        }

        .voice-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #2980B9 0%, #1F4E79 100%) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(52, 152, 219, 0.4) !important;
        }

        .voice-btn.active {
          background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%) !important;
          animation: voicePulse 1s ease-in-out infinite;
          box-shadow: 0 6px 25px rgba(255, 152, 0, 0.4) !important;
        }

        .voice-btn .btn-icon {
          background: rgba(255, 255, 255, 0.3) !important;
          color: white !important;
        }

        .voice-btn .btn-text {
          color: white !important;
          font-weight: bold !important;
        }

        /* 顔認証ボタン - ALSOK成功グリーン */
        .face-btn {
          background: ${componentThemes.reception.controls.face.background} !important;
          color: white !important;
          border: 2px solid ${alsokTheme.accent.success} !important;
          box-shadow: ${componentThemes.reception.controls.face.shadow} !important;
          min-height: ${componentThemes.reception.controls.face.minHeight} !important;
        }

        .face-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #1E8449 0%, #145A32 100%) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(39, 174, 96, 0.4) !important;
        }

        .face-btn.active {
          background: linear-gradient(135deg, #E91E63 0%, #C2185B 100%) !important;
          animation: facePulse 1s ease-in-out infinite;
          box-shadow: 0 6px 25px rgba(233, 30, 99, 0.4) !important;
        }

        .face-btn .btn-icon {
          background: rgba(255, 255, 255, 0.3) !important;
          color: white !important;
        }

        .face-btn .btn-text {
          color: white !important;
          font-weight: bold !important;
        }

        /* 会議室確認ボタン - ALSOKセカンダリブルー */
        .reservation-btn {
          background: ${componentThemes.reception.controls.reservation.background} !important;
          color: white !important;
          border: 2px solid ${alsokTheme.secondary.main} !important;
          box-shadow: ${componentThemes.reception.controls.reservation.shadow} !important;
          min-height: ${componentThemes.reception.controls.reservation.minHeight} !important;
        }

        .reservation-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, ${alsokTheme.secondary.dark} 0%, #2C5F6F 100%) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(74, 144, 164, 0.4) !important;
        }

        .reservation-btn .btn-icon {
          background: rgba(255, 255, 255, 0.3) !important;
          color: white !important;
        }

        .reservation-btn.disabled-maintenance {
          background: #6c757d !important;
          color: #adb5bd !important;
          border-color: #6c757d !important;
          cursor: not-allowed !important;
          opacity: 0.7 !important;
        }

        .reservation-btn.disabled-maintenance:hover {
          transform: none !important;
          box-shadow: none !important;
          background: #6c757d !important;
        }

        .reservation-btn.disabled-maintenance .btn-icon {
          background: rgba(173, 181, 189, 0.3) !important;
          color: #adb5bd !important;
        }

        .reservation-btn .btn-text {
          color: white !important;
          font-weight: bold !important;
        }

        .camera-control-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.6s ease;
        }

        .camera-control-btn:hover::before {
          left: 100%;
        }

        .btn-icon {
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(255,255,255,0.2);
          flex-shrink: 0;
        }

        .btn-text {
          font-size: 13px;
          font-weight: bold;
          line-height: 1.2;
          text-align: left;
        }

        .pulse-indicator {
          position: absolute;
          right: 8px;
          width: 8px;
          height: 8px;
          background: #ff6b6b;
          border-radius: 50%;
          animation: pulse 1s ease-in-out infinite;
        }

        .voice-btn {
          background: linear-gradient(135deg, ${alsokTheme.primary.light} 0%, ${alsokTheme.primary.main} 100%);
          color: white;
        }

        .voice-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.dark} 100%);
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.lg};
        }

        .voice-btn.active {
          background: linear-gradient(135deg, #ff6b6b 0%, #ff5252 100%);
          animation: voicePulse 1s ease-in-out infinite;
        }


        .camera-control-btn:disabled,
        .camera-control-btn.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          background: #cccccc !important;
        }

        .camera-control-btn:disabled::before,
        .camera-control-btn.disabled::before {
          display: none;
        }

        .camera-control-btn.disabled .btn-text {
          color: #666666;
        }

        @keyframes voicePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes facePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .side-panel {
          background: ${componentThemes.reception.sidebar.background};
          backdrop-filter: ${componentThemes.reception.sidebar.backdropFilter};
          border-left: ${componentThemes.reception.sidebar.borderLeft};
          display: flex;
          flex-direction: column;
          gap: ${alsokTheme.spacing.md};
          padding: ${alsokTheme.spacing.lg};
          overflow-y: auto;
          max-height: 100%;
        }

        .camera-section {
          text-align: center;
          margin-bottom: 6px;
        }

        .camera-container {
          position: relative;
          width: 100%;
          aspect-ratio: 4/3;
          border-radius: ${alsokTheme.borderRadius.lg};
          overflow: hidden;
          box-shadow: ${alsokTheme.shadows.lg};
          border: 3px solid ${alsokTheme.primary.main};
          background: ${alsokTheme.neutral.black};
        }

        /* aspect-ratioサポートがないブラウザ用の代替 */
        @supports not (aspect-ratio: 4/3) {
          .camera-container {
            height: 0;
            padding-bottom: 75%; /* 4:3のアスペクト比 */
          }
          
          .webcam-feed {
            position: absolute;
            top: 0;
            left: 0;
          }
        }

        .webcam-feed {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transform: scaleX(-1);
        }

        .camera-label {
          margin-top: 4px;
          font-size: 11px;
          color: ${alsokTheme.neutral.mediumGray};
          font-weight: bold;
        }

        .chat-section {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 300px;
          max-height: 450px;
        }

        .chat-history {
          flex: 1;
          overflow-y: auto;
          padding: ${alsokTheme.spacing.lg};
          background: ${alsokTheme.neutral.white};
          border-radius: ${alsokTheme.borderRadius.lg};
          display: flex;
          flex-direction: column;
          gap: ${alsokTheme.spacing.md};
          min-height: 250px;
          border: 1px solid ${alsokTheme.neutral.lightGray};
          box-shadow: ${alsokTheme.shadows.sm};
        }

        .chat-message {
          padding: 12px 16px;
          border-radius: 16px;
          animation: fadeIn 0.3s ease-in;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border: 1px solid rgba(0,0,0,0.05);
          margin-bottom: 8px;
        }

        .chat-message.user {
          background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%);
          color: white;
          margin-left: 40px;
          margin-right: 10px;
          border-top-right-radius: 4px;
        }

        .chat-message.ai {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          margin-right: 40px;
          margin-left: 10px;
          border-top-left-radius: 4px;
        }

        .chat-message.system {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          color: white;
          font-style: italic;
          text-align: center;
          margin: 8px 20px;
          border-radius: 20px;
          font-size: 13px;
          padding: 8px 12px;
        }

        .message-sender {
          font-size: 18px;
          margin-right: 10px;
          filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
        }

        .message-time {
          font-size: 14px;
          color: rgba(255,255,255,0.9);
          font-family: 'Courier New', monospace;
          float: right;
          margin-top: 2px;
          font-weight: 400;
        }
        
        .chat-message.system .message-time {
          color: rgba(255,255,255,0.9);
        }

        .message-content {
          font-size: 16px;
          line-height: 1.6;
          color: inherit;
          display: flex;
          align-items: flex-start;
          position: relative;
          font-weight: 500;
        }

        .error-display {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: ${alsokTheme.borderRadius.md};
          margin-top: 10px;
        }

        .error-icon {
          font-size: 16px;
        }

        .error-text {
          font-size: 15px;
          color: #dc2626;
          font-weight: 500;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .quick-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: auto;
          padding-top: 15px;
        }

        .quick-btn {
          padding: 12px 15px;
          border: none;
          border-radius: ${componentThemes.reception.quickActions.borderRadius};
          background: ${componentThemes.reception.quickActions.background};
          color: ${componentThemes.reception.quickActions.text};
          cursor: pointer;
          font-weight: 600;
          font-size: 15px;
          transition: ${alsokTheme.animation.transition};
          text-align: left;
          box-shadow: ${alsokTheme.shadows.md};
          border: 2px solid rgba(255, 255, 255, 0.2);
          min-height: ${componentThemes.reception.quickActions.minHeight};
        }

        .quick-btn:hover {
          background: ${componentThemes.reception.quickActions.hover};
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.lg};
        }

        .quick-btn.admin-contact {
          background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
          border: 2px solid #dc3545;
          color: white;
        }

        .quick-btn.admin-contact:hover {
          background: linear-gradient(135deg, #c82333 0%, #a02128 100%);
          border-color: #c82333;
          box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4);
          transform: translateY(-2px);
        }

        .quick-btn.admin-return {
          background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);
          border: 2px solid #28a745;
          color: white;
        }

        .quick-btn.admin-return:hover {
          background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
          border-color: #1e7e34;
          box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
          transform: translateY(-2px);
        }

        .visitor-info {
          background: ${alsokTheme.neutral.white};
          border: 2px solid ${alsokTheme.accent.secondary};
          border-radius: ${alsokTheme.borderRadius.lg};
          padding: ${alsokTheme.spacing.lg};
          margin-bottom: ${alsokTheme.spacing.lg};
          box-shadow: ${alsokTheme.shadows.lg};
          border-left: 4px solid ${alsokTheme.primary.main};
        }

        .visitor-info h3 {
          margin: 0 0 ${alsokTheme.spacing.md} 0;
          color: ${alsokTheme.primary.main};
          font-size: ${alsokTheme.typography.fontSize.lg};
          font-weight: ${alsokTheme.typography.fontWeight.bold};
          text-align: center;
        }

        .visitor-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
        }

        .info-row .label {
          font-weight: ${alsokTheme.typography.fontWeight.semibold};
          color: ${alsokTheme.primary.main};
          min-width: 60px;
        }

        .info-row .value {
          color: ${alsokTheme.neutral.darkGray};
          font-weight: ${alsokTheme.typography.fontWeight.medium};
          text-align: right;
          flex: 1;
        }

        .visitor-actions {
          display: flex;
          gap: 8px;
          margin-top: 10px;
        }

        .map-btn {
          flex: 1;
          padding: 8px;
          border: none;
          border-radius: ${alsokTheme.borderRadius.md};
          background: linear-gradient(135deg, ${alsokTheme.primary.light} 0%, ${alsokTheme.primary.main} 100%);
          color: white;
          cursor: pointer;
          font-size: 11px;
          font-weight: bold;
          transition: all 0.3s ease;
        }

        .map-btn:hover {
          background: linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.dark} 100%);
          transform: translateY(-1px);
        }

        .clear-visitor-btn {
          flex: 1;
          padding: 8px;
          border: none;
          border-radius: ${alsokTheme.borderRadius.md};
          background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
          color: white;
          cursor: pointer;
          font-size: 11px;
          font-weight: bold;
          transition: all 0.3s ease;
        }

        .clear-visitor-btn:hover {
          background: linear-gradient(135deg, #5a6268 0%, #343a40 100%);
          transform: translateY(-1px);
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        /* アクセシビリティ改善 */
        .camera-control-btn:focus,
        .quick-btn:focus,
        .clear-visitor-btn:focus {
          outline: 3px solid ${alsokTheme.primary.main};
          outline-offset: 2px;
        }
        
        .camera-control-btn:focus-visible,
        .quick-btn:focus-visible,
        .clear-visitor-btn:focus-visible {
          outline: 3px solid ${alsokTheme.primary.main};
          outline-offset: 2px;
        }
        
        /* 高コントラストモード対応 */
        @media (prefers-contrast: high) {
          .chat-message.user {
            background: #ffffff;
            border: 2px solid #0066cc;
          }
          
          .chat-message.ai {
            background: #f0f0f0;
            border: 2px solid #009900;
          }
          
          .visitor-info {
            border: 3px solid #ff6600;
            background: #fffacd;
          }
        }
        
        /* 動きを減らす設定に対応 */
        @media (prefers-reduced-motion: reduce) {
          .camera-control-btn,
          .quick-btn,
          .clear-visitor-btn {
            transition: none;
          }
          
          .pulse-indicator {
            animation: none;
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
          }
        }
        
        /* タブレット向けレスポンシブ */
        @media (max-width: 1024px) {
          .reception-content {
            grid-template-columns: 2fr 1fr;
          }
          
          .side-panel {
            max-width: 300px;
          }
          
          .camera-control-btn {
            min-height: 48px;
            font-size: 14px;
          }
          
          .quick-btn {
            min-height: 44px;
            font-size: 12px;
          }
        }
        
        /* スマートフォン向けレスポンシブ */
        @media (max-width: 768px) {
          .reception-content {
            grid-template-columns: 1fr;
            grid-template-rows: auto 1fr;
          }
          
          .side-panel {
            order: -1;
            max-height: 400px;
            overflow-y: auto;
          }
          
          .camera-controls {
            margin-top: 10px;
            flex-direction: row;
            gap: 8px;
          }
          
          .camera-control-btn {
            padding: 12px 16px;
            font-size: 12px;
            min-height: 48px;
            flex: 1;
          }
          
          .btn-icon {
            width: 24px;
            height: 24px;
            font-size: 16px;
          }
          
          .btn-text {
            font-size: 11px;
            line-height: 1.1;
          }
          
          .chat-section {
            max-height: 250px;
            min-height: 150px;
          }
          
          .chat-history {
            padding: 10px;
            gap: 8px;
          }
          
          .chat-message {
            padding: 8px 12px;
            font-size: 15px;
          }
          
          .visitor-info {
            padding: 12px;
            margin-bottom: 10px;
          }
          
          .info-row {
            font-size: 13px;
          }
          
          .quick-actions {
            gap: 6px;
          }
          
          .quick-btn {
            padding: 8px 12px;
            font-size: 13px;
            min-height: 44px;
          }
        }
        
        /* 極小画面対応 */
        @media (max-width: 480px) {
          .status-bar {
            padding: 10px 15px;
          }
          
          .logo {
            font-size: 18px;
          }
          
          .reception-title {
            font-size: 14px;
          }
          
          .current-time {
            font-size: 14px;
          }
          
          .camera-controls {
            flex-direction: column;
            gap: 6px;
          }
          
          .camera-control-btn {
            min-height: 52px;
          }
          
          .side-panel {
            max-height: 350px;
          }
        }

        /* ヘルプボタン */
        .help-button-container {
          z-index: 1000;
          user-select: none;
        }

        .help-btn {
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          border: none;
          border-radius: 25px;
          padding: 12px 20px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(255, 152, 0, 0.4);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          min-width: 120px;
          justify-content: center;
          white-space: nowrap;
          user-select: none;
        }

        .help-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 152, 0, 0.5);
          background: linear-gradient(135deg, #f57c00 0%, #e65100 100%);
        }

        .help-btn:active {
          transform: scale(0.98);
        }

        .help-icon {
          font-size: 16px;
          flex-shrink: 0;
        }

        .button-text {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          flex-shrink: 0;
        }

        /* ヘルプモーダル */
        .help-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
          animation: fadeIn 0.3s ease-out;
        }

        .help-modal {
          background: white;
          border-radius: ${alsokTheme.borderRadius.xl};
          max-width: 600px;
          width: 90%;
          max-height: 85vh;
          box-shadow: ${alsokTheme.shadows.xl};
          animation: slideUp 0.3s ease-out;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .help-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          border-bottom: 2px solid #f0f0f0;
          background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
          color: white;
          border-radius: ${alsokTheme.borderRadius.xl} ${alsokTheme.borderRadius.xl} 0 0;
          flex-shrink: 0;
        }

        .help-modal-header h2 {
          margin: 0;
          font-size: 20px;
          font-weight: bold;
        }

        .help-close-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
          transition: all 0.3s ease;
        }

        .help-close-btn:hover {
          background: rgba(255,255,255,0.3);
          transform: scale(1.1);
        }

        .help-modal-content {
          padding: 25px;
          flex: 1;
          overflow-y: auto;
        }

        .help-section {
          margin-bottom: 25px;
          padding: 15px;
          border-radius: ${alsokTheme.borderRadius.md};
          background: #f8f9fa;
        }

        .help-section.emergency {
          background: #fff3cd;
          border-left: 4px solid #ffc107;
        }

        .help-section h3 {
          margin: 0 0 10px 0;
          color: ${alsokTheme.primary.main};
          font-size: 16px;
          font-weight: bold;
        }

        .help-section p {
          margin: 0 0 10px 0;
          color: #333;
          line-height: 1.5;
        }

        .help-section ul {
          margin: 0;
          padding-left: 20px;
          color: #555;
        }

        .help-section li {
          margin: 5px 0;
          line-height: 1.4;
        }

        .help-modal-footer {
          padding: 20px 25px;
          text-align: center;
          border-top: 1px solid #e9ecef;
          flex-shrink: 0;
          background: white;
          border-radius: 0 0 ${alsokTheme.borderRadius.xl} ${alsokTheme.borderRadius.xl};
        }

        .help-ok-btn {
          background: linear-gradient(135deg, ${alsokTheme.primary.main} 0%, ${alsokTheme.primary.dark} 100%);
          color: white;
          border: 2px solid ${alsokTheme.primary.main};
          border-radius: ${alsokTheme.borderRadius.lg};
          padding: 16px 40px;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: ${alsokTheme.shadows.lg};
          margin-top: 10px;
          min-width: 160px;
        }

        .help-ok-btn:hover {
          background: linear-gradient(135deg, #1e7e34 0%, #155724 100%);
          transform: translateY(-2px);
          box-shadow: ${alsokTheme.shadows.lg};
        }
        }

        @media (max-width: 768px) {
          .ai-reception-transition {
            bottom: 15px;
            right: 15px;
          }
          
          .ai-reception-btn {
            padding: 12px 20px;
            font-size: 14px;
          }
        }
      `}</style>

      {/* ヘルプ・操作説明ボタン */}
      <div 
        className="help-button-container"
        style={{
          left: `${aiButtonPosition.x}px`,
          top: `${aiButtonPosition.y}px`,
          position: 'fixed'
        }}
      >
        <button 
          className="help-btn"
          onClick={handleHelpButtonClick}
          onMouseDown={handleHelpMouseDown}
          title="操作方法やヘルプを表示（ドラッグで移動可能）"
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <span className="help-icon">❓</span>
          <span className="button-text">ヘルプ</span>
        </button>
      </div>

      {/* ヘルプ・操作説明モーダル */}
      {isHelpModalOpen && (
        <div className="help-modal-overlay">
          <div className="help-modal">
            <div className="help-modal-header">
              <h2>📖 操作説明・ヘルプ</h2>
              <button 
                className="help-close-btn"
                onClick={() => setIsHelpModalOpen(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="help-modal-content">
              <div className="help-section">
                <h3>🎤 音声で話しかける</h3>
                <p>「音声で話しかける」ボタンを押して、マイクに向かってお話しください。</p>
                <ul>
                  <li>「会議室を予約したいです」</li>
                  <li>「田中さんとお約束があります」</li>
                  <li>「○○会議室はどこですか？」</li>
                </ul>
              </div>
              
              <div className="help-section">
                <h3>📸 顔パス認証</h3>
                <p>事前に顔を登録されている方は、カメラの前に立つだけで自動認証されます。</p>
                <ul>
                  <li>カメラの中央に顔を合わせてください</li>
                  <li>認証が完了すると自動で案内されます</li>
                </ul>
              </div>
              
              <div className="help-section">
                <h3>📋 来客登録</h3>
                <p>初回の方や予約がない方は、来客登録からお手続きください。</p>
                <ul>
                  <li>お名前・会社名を入力</li>
                  <li>面会者・部署を選択</li>
                  <li>来訪目的を選択</li>
                </ul>
              </div>
              
              <div className="help-section">
                <h3>📅 会議室確認</h3>
                <p>会議室の空き状況確認や予約ができます。</p>
              </div>
              
              <div className="help-section">
                <h3>📞 その他のサポート</h3>
                <ul>
                  <li><strong>内線電話:</strong> 受付スタッフにお繋ぎします</li>
                  <li><strong>管理者に連絡:</strong> 緊急時や操作でお困りの際はこちら</li>
                </ul>
              </div>
              
              <div className="help-section emergency">
                <h3>🆘 緊急時・お困りの際</h3>
                <p>操作がうまくいかない場合や緊急時は、受付スタッフまでお声がけください。</p>
              </div>
            </div>
            
            <div className="help-modal-footer">
              <button 
                className="help-ok-btn"
                onClick={() => setIsHelpModalOpen(false)}
              >
                理解しました
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  )
}

export default ReceptionLayout